<script setup lang="ts">
import {
  IonModal,
  IonTextarea,
  loadingController,
} from '@ionic/vue'
import { ref } from 'vue'
import ChapterPlotSuggest from './ChapterPlotSuggest.vue'
import CommonButton from '@/components/CommonButton.vue'
import type { Chat, Message } from '@/types'
import { eventService } from '@/services/event'
import type { CreatedEventModel } from '@/types/universe'

interface Props {
  chat: Chat
  messages: Message[]
  isPreview: boolean
  open: boolean
}

interface Emits {
  (e: 'applyEvent', event: string): void
  (e: 'close', event?: CreatedEventModel): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const eventValue = ref('')

async function handleCreateEvent() {
  const loading = await loadingController.create({
    message: 'Creating event...',
  })
  try {
    const res = await eventService.createEvent({
      universeId: props.chat.universe.universeId,
      eventDescription: eventValue.value,
      eventOrder: props.chat.universe.events.length,
      preview: props.isPreview,
    })
    loading.present()
    // close the modal
    emit('close', res)
    eventValue.value = ''
  }
  catch (err) {
    console.error(err)
  }
  finally {
    loading.dismiss()
  }
}

async function canDismiss(data?: any, role?: string) {
  return role !== 'gesture'
}
</script>

<template>
  <ion-modal
    :is-open="open"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    class="custom-ion-modal"
    :backdrop-dismiss="false"
    :handle="false"
    :can-dismiss="canDismiss"
    @did-dismiss="
      () => {
        emit('close');
      }
    "
    @ion-modal-did-dismiss="
      () => {
        emit('close');
      }
    "
  >
    <div class="px-4 pb-unsafe-area max-h-[100vh] overflow-y-auto">
      <div class="flex justify-between pb-4 items-center">
        <div class="color-#fff text-4 leading-normal" @click="$emit('close')">
          Cancel
        </div>
        <h4 class="text-4.5 fw-700 leading-normal">
          What happens next?
        </h4>
        <common-button
          text="Apply"
          :bg-color="eventValue.length > 0 ? 'bg-#00F4FF' : 'bg-secondary'"
          :text-color="eventValue.length > 0 ? 'text-on-primary' : 'text-on-secondary'"
          :disabled="eventValue.length === 0"
          @click="
            () => {
              if (eventValue.length > 0) {
                handleCreateEvent();
              }
            }
          "
        />
      </div>
      <p class="text-3.5 fw-400 color-#BDBDBD pb-2">
        Write what you want to happen next...
      </p>
      <ion-textarea
        v-model="eventValue"
        :rows="4"
        :maxlength="1000"
        class="rounded-2 pl-4 pr-2 py-1 bg-input-bg-2 mb-2"
        autocomplete="on"
        autocorrect="on"
        autocapitalize="on"
        placeholder="Enter your custom plot here, or select a suggested one below."
      />
      <p class="text-3.5 fw-400 color-#BDBDBD py-2">
        or Select one plot option
      </p>
      <chapter-plot-suggest
        :chat="chat"
        :messages="messages"
        :is-preview="isPreview"
        @apply-event="
          ($event) => {
            eventValue = $event.eventDescription || '';
          }
        "
      />
      <div class="h-4" />
    </div>
  </ion-modal>
</template>

<style scoped>
textarea {
  resize: none;
}

ion-button {
  --padding: 0;
}
ion-modal {
  --height: auto;
}

.custom-ion-modal  {
  background-color: rgba(0, 0, 0, 0.8);
}
</style>
